import React, { useState } from 'react';
import { AlertTriangle, Upload, Download, Search, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useClaimants } from '@/hooks/cmm/useClaimants';
import type { Claimant, TortCode } from '@/types/cmm/claimant.types';

// ── Presentational lookup: resolves crossLitigationMatchIds → tortCode for badges.
// Mirrors MOCK_CLAIMANTS in useClaimants.ts. Replace with API lookup when live.
const CLAIMANT_TORT_LOOKUP: Record<string, TortCode> = {
  '3510738-vga': 'VGA',
  '3511035-vga': 'VGA',
  '3455940-vga': 'VGA',
  '3519226-vga': 'VGA',
  '3507066-vga': 'VGA',
  '3516024-vga': 'VGA',
  '3458717-vga': 'VGA',
  '3559439-vga': 'VGA',
};

const TORT_BADGE_COLORS: Record<string, string> = {
  VGA:  'bg-blue-100 text-blue-800',
  SMA:  'bg-purple-100 text-purple-800',
  RLP:  'bg-green-100 text-green-800',
  PFAS: 'bg-amber-100 text-amber-800',
};

function getBadgeColor(code: string): string {
  return TORT_BADGE_COLORS[code] ?? 'bg-gray-100 text-gray-800';
}

function resolveOverlapBadges(claimant: Claimant): TortCode[] {
  if (!claimant.crossLitigationFlag || !claimant.crossLitigationMatchIds?.length) {
    return [];
  }
  return claimant.crossLitigationMatchIds
    .map(id => CLAIMANT_TORT_LOOKUP[id])
    .filter((code): code is TortCode => Boolean(code));
}

const TORT_OPTIONS: TortCode[] = ['SMA', 'VGA', 'RLP', 'PFAS'];

export default function ClaimantSpreadsheet() {
  const [activeTort, setActiveTort] = useState<TortCode>('VGA');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { claimants, total, isLoading, error, filters, setFilters } =
    useClaimants(activeTort);

  const handleTortChange = (tort: TortCode) => {
    setActiveTort(tort);
    setFilters({ tortCode: tort });
    setDropdownOpen(false);
    setSelectedIds(new Set());
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ searchQuery: e.target.value });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(
      selectedIds.size === claimants.length
        ? new Set()
        : new Set(claimants.map(c => c.id))
    );
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-4">
        <span>Home</span>
        <span className="mx-2">›</span>
        <span>Claimants</span>
        <span className="mx-2">›</span>
        <span className="text-gray-900 font-medium">Spreadsheet View</span>
      </nav>

      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Spreadsheet View</h1>
          <p className="text-sm text-gray-500 mt-1">
            {total} claimants — {activeTort}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Tort selector dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(o => !o)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {activeTort}
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                {TORT_OPTIONS.map(t => (
                  <button
                    key={t}
                    onClick={() => handleTortChange(t)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      t === activeTort
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Upload className="w-4 h-4" />
            Import CSV
          </button>
          <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search by name or email..."
          defaultValue={filters.searchQuery ?? ''}
          onChange={handleSearch}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="w-10 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={claimants.length > 0 && selectedIds.size === claimants.length}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              {['NAME', 'TORT', 'STATE', 'STAGE', 'OVERLAP', 'DEFICIENCY', 'CREATED', 'ACTIONS'].map(
                col => (
                  <th
                    key={col}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    {col}
                  </th>
                )
              )}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                  Loading claimants…
                </td>
              </tr>
            )}
            {!isLoading && error && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-red-500">
                  {error}
                </td>
              </tr>
            )}
            {!isLoading && !error && claimants.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                  No claimants found.
                </td>
              </tr>
            )}
            {!isLoading &&
              !error &&
              claimants.map(c => {
                const overlapBadges = resolveOverlapBadges(c);
                return (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        className="rounded border-gray-300"
                      />
                    </td>

                    {/* NAME */}
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {c.firstName} {c.lastName}
                    </td>

                    {/* TORT */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getBadgeColor(c.tortCode)}`}
                      >
                        {c.tortCode}
                      </span>
                    </td>

                    {/* STATE */}
                    <td className="px-4 py-3 text-gray-700">{c.state}</td>

                    {/* STAGE */}
                    <td className="px-4 py-3 text-gray-700">{c.pipelineStage}</td>

                    {/* OVERLAP — cross-litigation tort badges */}
                    <td className="px-4 py-3">
                      {overlapBadges.length > 0 ? (
                        <div className="flex gap-1 flex-wrap">
                          {overlapBadges.map((code, i) => (
                            <span
                              key={i}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getBadgeColor(code)}`}
                            >
                              {code}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* DEFICIENCY */}
                    <td className="px-4 py-3">
                      {c.crossLitigationFlag ? (
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* CREATED */}
                    <td className="px-4 py-3 text-gray-500">{formatDate(c.createdAt)}</td>

                    {/* ACTIONS */}
                    <td className="px-4 py-3">
                      <Link
                        to={`/claimants/${c.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {/* Table footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
          <span>{total} total claimants</span>
          <div className="flex items-center gap-3">
            <button className="text-gray-400 hover:text-gray-600 disabled:opacity-40" disabled>
              Prev
            </button>
            <span className="text-gray-700 font-medium">Page 1</span>
            <button className="text-gray-400 hover:text-gray-600 disabled:opacity-40" disabled>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}